import { FC, useEffect, useRef, useState } from 'react';
import { Box, Image, List, ListItem, Skeleton, Text } from 'src/components/ui';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface IMessageProps {
	content: string;
	onImageClick?: (src: string) => void;
	onContentResize?: () => void;
}

const Message: FC<IMessageProps> = ({
	content,
	onImageClick,
	onContentResize,
}) => {
	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!contentRef.current || !onContentResize) return;

		const resizeObserver = new ResizeObserver(() => {
			onContentResize();
		});

		resizeObserver.observe(contentRef.current);

		return () => {
			resizeObserver.disconnect();
		};
	}, [onContentResize]);

	const transformContentWithLink = (rawContent: string): string => {
		if (typeof rawContent !== 'string') {
			console.warn(
				'transformContentWithLink: rawContent is not a string:',
				rawContent,
			);
			return '';
		}

		return rawContent.replace(
			/<link\s+href="(.*?)">(.*?)<\/link>/g,
			'[$2]($1)',
		);
	};

	return (
		<div ref={contentRef}>
			<ReactMarkdown
				remarkPlugins={[remarkGfm]}
				components={{
					img: ({ src, alt }) => (
						<LazyImage
							src={src as string}
							alt={alt}
							onImageClick={onImageClick}
						/>
					),
					p: ({ children }) => <Text fontSize="14px">{children}</Text>,
					a: ({ href, children }) => (
						<Text
							as="a"
							href={href}
							target="_blank"
							rel="noopener noreferrer"
							color="blue.500"
							textDecoration="underline"
						>
							{children}
						</Text>
					),

					ul: ({ children }) => (
						<List spacing={2} styleType="disc" pl={4}>
							{children}
						</List>
					),
					ol: ({ children }) => (
						<List as="ol" spacing={2} styleType="decimal" pl={4}>
							{children}
						</List>
					),
					li: ({ children }) => <ListItem>{children}</ListItem>,
				}}
			>
				{transformContentWithLink(content)}
			</ReactMarkdown>
		</div>
	);
};

const LazyImage: FC<{
	src: string;
	alt?: string;
	onImageClick?: (src: string) => void;
}> = ({ src, alt, onImageClick }) => {
	const [isLoaded, setIsLoaded] = useState(false);

	return (
		<Box
			position="relative"
			borderRadius="lg"
			overflow="hidden"
			cursor="pointer"
			maxW="200px"
		>
			{!isLoaded && <Skeleton h="150px" w="full" />}

			<Image
				src={src}
				alt={alt}
				onLoad={() => setIsLoaded(true)}
				onClick={onImageClick ? () => onImageClick(src) : undefined}
				display={isLoaded ? 'block' : 'none'}
				borderRadius="lg"
				objectFit="cover"
			/>
		</Box>
	);
};

export default Message;
